import * as Sentry from '@sentry/react';

import { BANK_CREDENTIAL_PRIVACY } from '../constants/privacy';
import logger from 'itrvl-logger';
import { matchPath } from 'react-router-dom';
import {
  redactSentryEvent,
  redactRecordingEvent,
  installRecordedUrlRedaction,
  CREDENTIAL_BODY_URLS,
} from 'itrvl-types/src/sentryRedaction';

const log = logger(__filename);

export const setupSentry = (tag, history, routes) => {
  const dsn = tag === 'agent' ? process.env.REACT_APP_SENTRY_AGENT_DSN : process.env.REACT_APP_SENTRY_CLIENT_DSN;
  const environment = process.env.REACT_APP_ITRVL_ENV;

  // Don't setup sentry on integration
  if (environment === 'integration') return;

  log.debug(`Setup Sentry Browser, environment: ${environment}, dsn: ${dsn}`);
  if (dsn) {
    // The api's sampler returns the incoming sampled flag whenever one is present, so an unsampled
    // page trace forces a hard 0 on the backend half. This rate decides whether a backend
    // transaction exists at all. One name covers agent and client, which share this file.
    const tracesSampleRate = Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0.01);
    let replaySampleRate;
    switch (environment) {
      case 'development':
        replaySampleRate = 1;
        break;
      default:
        replaySampleRate = 0.1;
        break;
    }
    const config = {
      dsn,
      environment,
      release: process.env.REACT_APP_VERSION,
      // Outgoing requests matching these carry the sentry-trace header, so the api can continue the
      // browser's trace. Needs "Access-Control-Allow-Headers: sentry-trace" on the receiving side.
      tracePropagationTargets: [
        'localhost',
        'localhost:3001',
        'http://localhost:3000',
        'http://127.0.0.1:3001',
        'http://localhost:3001',
        /cdn-static.*itrvl.com/,
        /api.*itrvl.com/,
        /.*itrvl.com/,
        /^\//,
      ],
      integrations: [
        // A magic link puts a credential in the page URL, so the recorder writes it into the page
        // href and into each link it serialises. `beforeAddRecordingEvent` reaches neither — the SDK
        // gates it on rrweb Custom events — so the recording needs its own redaction.
        installRecordedUrlRedaction(
          Sentry.replayIntegration({
            maskAllText: false,
            maskAllInputs: false,
            inlineImages: false,
            blockAllMedia: false,
            // `maskAllInputs` is off, so an input is recorded unless something blocks it. Adyen's
            // ACH component takes the account and routing numbers in plain inputs, so the fields
            // carrying them are blocked by attribute rather than masked: a blocked element is never
            // serialised into the recording at all.
            block: [BANK_CREDENTIAL_PRIVACY.selector],
            networkDetailAllowUrls: [/https:\/\/.*itrvl.com\/api/, 'http://localhost:3000/api', 'http://localhost:3002/api'],
            // The allow list above is a prefix match on /api, so it covers every credential-bearing
            // form post as well. `networkCaptureBodies` defaults to true, and the request body of a
            // login, a reset or a set-password carries the plaintext the user typed. A deny match
            // drops only the request and response detail: the span keeps its url, method, status and
            // both timestamps, so the flow stays on the replay timeline.
            networkDetailDenyUrls: CREDENTIAL_BODY_URLS,
            // The breadcrumb and performanceSpan events replay synthesises, which carry the
            // navigation and network URLs.
            beforeAddRecordingEvent: redactRecordingEvent,
          }),
        ),
        // One integration covers both tracing and react-router v5 navigation naming; it takes the
        // browser tracing options alongside the router ones.
        Sentry.reactRouterV5BrowserTracingIntegration({
          history,
          routes,
          matchPath,
          idleTimeout: 5000,
        }),
      ],
      tracesSampleRate,
      replaysSessionSampleRate: replaySampleRate,
      replaysOnErrorSampleRate: 1,
      // Benign browser advisory, both wordings (pre/post Chrome 88): resize-callback
      // layout work spilled into the next frame; the observer re-fires there, nothing is lost
      ignoreErrors: ['ResizeObserver loop limit exceeded', 'ResizeObserver loop completed with undelivered notifications'],
      debug: false,
    };
    Sentry.init(config);
    // A magic link is a bearer credential in the page URL, so the browser carries one in
    // event.request.url, in breadcrumb urls, in the Referer and in the replay event's own url list.
    // An event processor is what reaches all of those: beforeSend applies only to error and message
    // events, and the Replay integration never enters the path that runs it.
    Sentry.addEventProcessor(redactSentryEvent);
    Sentry.setTag('package', tag);
    Sentry.setTag('rrweb', 'yes');
  }
};

// The scope tags a masquerade writes, listed once so the set and the clear cannot drift apart. A tag
// added to one and not the other outlives the masquerade and names it on every later event.
const MASQUERADE_TAGS = ['agencyCode', 'masquerade', 'masqueradeFrom'];

// The user is the person being acted AS. Sentry counts users affected off the email, so a composed
// superadmin-and-target string makes every pair read as a separate person. The superadmin travels as
// a tag, which is indexed and can be searched and grouped on its own. This is the shape the api
// reports, so both halves of one session name the same two people.
export const setMasqueradeScope = (masqueradeUser, agencyCode, superadminEmail) => {
  Sentry.getCurrentScope()
    .setUser({ email: masqueradeUser })
    .setTag('agencyCode', agencyCode)
    // A string, not a boolean: a boolean reaches Sentry as 'True', so `masquerade:true` — the query
    // anyone would write, and the one the api answers to — matches nothing.
    .setTag('masquerade', 'true')
    .setTag('masqueradeFrom', superadminEmail);
};

// Nothing reloads the page when a masquerade ends, so the scope survives and every tag has to be
// cleared by hand. The user is not restored here: the caller re-identifies the original user, which
// is the same write that runs on an ordinary login.
export const clearMasqueradeScope = () => {
  const scope = Sentry.getCurrentScope();
  MASQUERADE_TAGS.forEach(tag => scope.setTag(tag, undefined));
};
